import { Button, Grid, Popover, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import React, { MouseEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import { OrderDirection } from '../Datatable/OrderDirection';
import _ from 'lodash';
import { MarketInfoSearchSelector, setDatatableSelectedMarketInfoIndex, setOrderBy, setOrderDirection, setTriggerMarketInfoSearch } from './reducers/MarketInfoSearchReducer';
import { MarketInfoInputSelector, setSelectedMarketInfoId } from './reducers/MarketInfoInputReducer';
import MarketInfoModel from './models/MarketInfoModel';
import { userProfileSelector } from '../Login/reducers/UserProfileReducer';
import Swal from 'sweetalert2';
import MarketInfoApi, { DeleteMarketInfoByIdParam } from './api/MarketInfoApi';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { UserGroupEnum } from '../UserManagement/UserGroupEnum';

const MarketInfoTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();
    let marketInfoData = useSelector(MarketInfoSearchSelector);
    const marketInfoInputViewData = useSelector(MarketInfoInputSelector);
    const userProfile = useSelector(userProfileSelector);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [overviewFullTc, setOverviewFullTc] = useState<string>("");

    const columns = [
        {
            Field: "tonnageVesselName",
            Title: "Vessel",
            AllowSort: true,
        } as DatatableColumnModel<MarketInfoModel>,
        {
            Field: "size",
            Title: "Size",
            AllowSort: true,
            Render: (rowData: MarketInfoModel) => {
                return <>{rowData.size ? rowData.size.toLocaleString() : ""}</>;
            }
        } as DatatableColumnModel<MarketInfoModel>,
        {
            Field: "type",
            Title: "Biz",
            AllowSort: true,
        } as DatatableColumnModel<MarketInfoModel>,
        {
            Field: "cargoTypeName",
            Title: "Cargo",
            AllowSort: true,
        } as DatatableColumnModel<MarketInfoModel>,
        {
            Field: "loadPortName",
            Title: "L/D Port",
            AllowSort: true,
            Render: (rowData: MarketInfoModel) => {
                return <>
                    <span
                        onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
                            if (rowData.loadPortArea && rowData.loadPortArea.length > 0) {
                                setAnchorEl(event.currentTarget);
                                setOverviewFullTc(rowData.loadPortArea);
                            }
                        }}
                        onMouseLeave={() => {
                            setAnchorEl(null)
                        }}
                    >
                        {rowData.loadPortName}
                    </span>
                </>
            }
        } as DatatableColumnModel<MarketInfoModel>,
        {
            Field: "disportName",
            Title: "D/R Port",
            AllowSort: true,
            Render: (rowData: MarketInfoModel) => {
                return <>
                    <span
                        onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
                            if (rowData.disportArea && rowData.disportArea.length > 0) {
                                setAnchorEl(event.currentTarget);
                                setOverviewFullTc(rowData.disportArea);
                            }
                        }}
                        onMouseLeave={() => {
                            setAnchorEl(null)
                        }}
                    >
                        {rowData.disportName}
                    </span>
                </>
            }
        } as DatatableColumnModel<MarketInfoModel>,
        {
            Field: "ownerRate",
            Title: "Owners $",
            AllowSort: true,
            Render: (rowData: MarketInfoModel) => {
                return <>{rowData.ownerRate ? rowData.ownerRate.toLocaleString() : ""}</>;
            }
        } as DatatableColumnModel<MarketInfoModel>,
        {
            Field: "chartererRate",
            Title: "Chtrs $",
            AllowSort: true,
            Render: (rowData: MarketInfoModel) => {
                return <>{rowData.chartererRate ? rowData.chartererRate.toLocaleString() : ""}</>;
            }
        } as DatatableColumnModel<MarketInfoModel>,
        {
            Field: "ownerName",
            Title: "Owners",
            AllowSort: true,
        } as DatatableColumnModel<MarketInfoModel>,
        {
            Field: "chartererName",
            Title: "Chtrs",
            AllowSort: true,
        } as DatatableColumnModel<MarketInfoModel>,
        {
            Field: "userDisplayName",
            Title: "Updated By",
            AllowSort: true,
        } as DatatableColumnModel<MarketInfoModel>,
        {
            Field: "updatedDate",
            Title: "Updated Date",
            AllowSort: true,
            Render: (rowData: MarketInfoModel) => (
                <>
                    {formatUpdatedDate(rowData.updatedDate)}
                </>
            )
        } as DatatableColumnModel<MarketInfoModel>,
        {
            Field: "",
            Title: "",
            AllowSort: true,
            Render: (rowData: MarketInfoModel) => (
                <>
                    {userProfile.userGroup === UserGroupEnum.Administrator &&
                        <Tooltip title="Delete Cargo">
                            <Button
                                className='table-action-button'
                                variant='outlined'
                                color='error'
                                onClick={() => {
                                    deleteMarketInfo(rowData.id)
                                }}
                            >
                                <i className="fa fa-trash" aria-hidden="true"></i>
                            </Button>
                        </Tooltip>
                    }
                </>
            )
        } as DatatableColumnModel<MarketInfoModel>,

    ];

    const deleteMarketInfo = (id: number) => {
        Swal.fire({
            title: 'Are you sure want to delete this market info? This action can not be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));
                MarketInfoApi.Delete({
                    id: id
                } as DeleteMarketInfoByIdParam)
                    .then((response: BaseResponse<MarketInfoModel>) => {
                        if (response.success) {
                            Utility.Alert.Success("Market info deleted successful");

                            dispatch(showLoading(false));

                            dispatch(setTriggerMarketInfoSearch(new Date().getTime()));
                        }
                    });
            }
        })
    }

    const formatDateMMMDD = (dateString: string): string => {
        let date = new Date(dateString);
        return moment(date).format("DD MMM");
    };

    const formatUpdatedDate = (dateString: string): string => {
        let date = new Date(dateString);
        return moment(date).format("DD/MM/YYYY HH:mm");
    };

    const getTableClassName = () => {
        let className = "market-info-datatable-search-result nice-scroll";

        if (!marketInfoInputViewData.inputExpand) {
            className += " market-info-datatable-search-result-all-input-collapse";
        }

        return className;
    }

    return <div className='datatable-wrapper'>
        <Grid
            container
            justifyContent="flex-end"
            className="table-search-result-utility-bar"
        >
            <Grid
                className='table-search-result-utility-bar-item table-search-result-utility-bar-result-found'
            >
                {marketInfoData.searchResultTotalRecord + " results found"}
            </Grid>
        </Grid>
        <Datatable<MarketInfoModel>
            useDefaultSearchBar={false}
            pagingType={PagingTypeEnum.None}
            data={marketInfoData.searchResults}
            useItemPerPage={false}
            columns={columns}
            isDataLoaded={true}
            className={getTableClassName()}
            useHeaderSort={true}
            onOrderDirectionChange={(column: DatatableColumnModel<MarketInfoModel>, orderDirection: OrderDirection) => {
                dispatch(setOrderBy(column.Field));
                dispatch(setOrderDirection(orderDirection));
                dispatch(setTriggerMarketInfoSearch((new Date()).getTime()));
            }}
            onRowSelectedChange={(tonnage: MarketInfoModel, index: number, event: MouseEvent<HTMLElement>) => {
                var target = event.target as HTMLElement;
                if (target.tagName !== "BUTTON") {
                    dispatch(setSelectedMarketInfoId(tonnage.id))
                    dispatch(setDatatableSelectedMarketInfoIndex(index));
                }
            }}
            selectedRowIndex={marketInfoData.datatableSelectedIndex}
            stickyHeader={true}
            orderColumn={marketInfoData.query.orderBy}
            orderDirection={marketInfoData.query.orderDirection}
        ></Datatable>
        <Popover
            id="mouse-over-popover"
            sx={{
                pointerEvents: 'none',
            }}
            open={anchorEl != null}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            disableRestoreFocus
        >
            <Typography sx={{ p: 1 }}>{overviewFullTc}</Typography>
        </Popover>
    </div>
};

export default MarketInfoTable;